import React, { useMemo, useState } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useLocation, useNavigate } from 'react-router-dom';
import Collapse from '@mui/material/Collapse';
import ArrowUpIcon from '@mui/icons-material/ArrowDropUpOutlined';
import ArrowDownIcon from '@mui/icons-material/ArrowDropDownOutlined';
import { Box, Tooltip } from '@mui/material';
import { useSidebarContext } from './SidebarContext';
import { NavigationItemType } from 'framework/navigation';
import NavigationItemLabel from 'framework/ui/menu/NavigationItemLabel';

type SidebarItemProps = {
  clickHandler?: (name: string, isOpen: boolean) => void;
} & NavigationItemType;

// Utility: determines if current menu item is active
const isMenuItemActive = (urls: string[], pathname: string, exceptionUrls: string[], exact: boolean) => {
  if (!urls) return false;
  if (exceptionUrls?.some(url => pathname.includes(url))) return false;
  return urls.some(url => (exact ? pathname === url : pathname === url || pathname.includes(url)));
};

const SidebarItem: React.FC<SidebarItemProps> = ({
  name,
  url,
  children,
  icon,
  isActive = false,
  clickHandler = () => {},
  urls = [],
  exact = false,
  exceptionUrls = [],
  isAlpha,
  isBeta,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleDrawerToggle = () => {}, collapsed, isDesktop, setCollapsed } = useSidebarContext();

  const isActiveMenu = useMemo(
    () => isMenuItemActive(urls, location.pathname, exceptionUrls || [], exact),
    [location.pathname, urls, exceptionUrls, exact],
  );

  const [isOpen, setIsOpen] = useState(isActive);
  const isCollapsible = !!children?.length;

  // --- Derived booleans to simplify JSX ---
  const isCollapsedDesktop = collapsed && isDesktop;
  const shouldShowCollapseContent = (!collapsed && isDesktop) || !isDesktop;
  const showTooltip = isCollapsedDesktop;
  const showTextAndArrows = !isCollapsedDesktop;

  // --- Handlers ---
  const handleClick = (e?: React.MouseEvent) => {
    if (!e) return;

    // Check if Ctrl/Cmd was pressed
    const isCtrlClick = e.ctrlKey || e.metaKey;
    const isMiddleClick = e.button === 1; // Middle mouse button

    // enable default behavior
    if ((isCtrlClick || isMiddleClick) && url) {
      // Do not call preventDefault() and navigate()
      clickHandler(name, false);
      return;
    }

    // For a regular click - we prevent the default and handle it ourselves
    e.preventDefault();

    if (isCollapsible) {
      // Expand sidebar on desktop if collapsed
      if (isCollapsedDesktop) {
        setCollapsed!(false);
        setIsOpen(true);
        clickHandler(name, true);
      } else {
        setIsOpen(prev => !prev);
        clickHandler(name, !isOpen);
      }
    } else {
      // Navigate and close drawer for simple items
      if (url) navigate(url);
      handleDrawerToggle();
      clickHandler(name, false);
    }
  };

  const renderItems = (items: SidebarItemProps[]) =>
    items.map(child => <SidebarItem key={`${child.url}-${child.name}`} {...child} clickHandler={clickHandler} />);

  // --- Collapsible item (has children) ---
  if (isCollapsible) {
    const innerButton = (
      <ListItemButton
        onClick={handleClick}
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: showTextAndArrows ? 'flex-start' : 'center',
          px: 1,
          py: 0.5,
        }}
      >
        {icon && (
          <ListItemIcon
            sx={{
              minWidth: 36,
              justifyContent: 'center',
              'svg > path': {
                fill: 'var(--mui-palette-common-sidebarMenuText)',
              },
            }}
          >
            {typeof icon === 'string' ? <i className={icon} /> : icon}
          </ListItemIcon>
        )}

        {/* Render name + arrow only when sidebar is expanded */}
        {showTextAndArrows && (
          <>
            <ListItemText primary={name} sx={{ marginRight: '4px' }} />
            {isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
          </>
        )}
      </ListItemButton>
    );

    const buttonWithOptionalTooltip = showTooltip ? (
      <Tooltip arrow placement="right" title={name}>
        <Box component="span" sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          {innerButton}
        </Box>
      </Tooltip>
    ) : (
      innerButton
    );

    return (
      <>
        <ListItem disablePadding sx={{ mt: shouldShowCollapseContent && isOpen ? 1.5 : 0, transition: 'margin 0.12s' }}>
          {buttonWithOptionalTooltip}
        </ListItem>

        {/* Nested items only when expanded */}
        {shouldShowCollapseContent && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit sx={{ mb: isOpen ? 1.5 : 0 }}>
            <List component="div" disablePadding>
              {renderItems(children || [])}
            </List>
          </Collapse>
        )}
      </>
    );
  }

  // --- Simple item (no children) ---
  if (isCollapsedDesktop) {
    const buttonContent = (
      <ListItemButton
        onClick={handleClick}
        component={url ? 'a' : 'div'}
        href={url || undefined}
        sx={{
          justifyContent: 'center',
          px: 1,
          ...(url ? { textDecoration: 'none', color: 'inherit' } : {}),
        }}
        className={isActiveMenu ? 'active' : ''}
      >
        {icon ? (
          <ListItemIcon
            sx={{
              minWidth: 36,
              justifyContent: 'center',
              'svg > path': {
                fill: 'var(--mui-palette-common-sidebarMenuText)',
              },
            }}
          >
            {typeof icon === 'string' ? <i className={icon} /> : icon}
          </ListItemIcon>
        ) : (
          <ListItemText sx={{ textAlign: 'center' }} primary={name.slice(0, 3)} />
        )}
      </ListItemButton>
    );

    return (
      <ListItem disablePadding>
        {showTooltip ? (
          <Tooltip arrow placement="right" title={name}>
            <Box component="span" sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              {buttonContent}
            </Box>
          </Tooltip>
        ) : (
          buttonContent
        )}
      </ListItem>
    );
  }

  // --- Expanded simple item ---
  return (
    <ListItem disablePadding>
      <ListItemButton
        onClick={handleClick}
        component={url ? 'a' : 'div'}
        href={url || undefined}
        sx={url ? { textDecoration: 'none', color: 'inherit' } : {}}
        className={isActiveMenu ? 'active' : ''}
      >
        {icon && (
          <ListItemIcon
            sx={{
              minWidth: 36,
              justifyContent: 'center',
              'svg > path': {
                fill: 'var(--mui-palette-common-sidebarMenuText)',
              },
            }}
          >
            {typeof icon === 'string' ? <i className={icon} /> : icon}
          </ListItemIcon>
        )}
        <ListItemText primary={<NavigationItemLabel name={name} url={url} isAlpha={isAlpha} isBeta={isBeta} />} />
      </ListItemButton>
    </ListItem>
  );
};

export default SidebarItem;
